import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchWorkoutTypes, 
  fetchWorkouts,
  addWorkout,
  deleteWorkout,
  addWorkoutType,
  deleteWorkoutType,
} from '../../features/fitness/fitnessSlice';
import { Add as AddIcon, Delete as DeleteIcon, PictureAsPdf as PdfIcon, Image as ImageIcon } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Fitness = () => {
  const dispatch = useDispatch();
  const [calendar, setCalendar] = useState([]);
  const { workoutTypes, workouts, loading } = useSelector(state => state.fitness);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const years = Array.from({ length: 10 }, (_, i) => selectedYear - 5 + i);
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeColor, setNewTypeColor] = useState('#000000');
  const [selectedDate, setSelectedDate] = useState(null);
  const [workoutSelectOpen, setWorkoutSelectOpen] = useState(false);

  // 添加星期几的标题
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // 计算每月第一天是星期几
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // 生成月份的日历数据，确保总是6行
  const generateMonthData = (year, month) => {
    const firstDay = getFirstDayOfMonth(year, month);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = Array(firstDay).fill(null);
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    const remainingDays = 42 - days.length;
    days.push(...Array(remainingDays).fill(null));
    
    return days;
  };

  useEffect(() => {
    const months = Array.from({ length: 12 }, (_, i) => ({
      name: new Date(selectedYear, i).toLocaleString('default', { month: 'long' }),
      days: new Date(selectedYear, i + 1, 0).getDate()
    }));
    setCalendar(months);
    
    const loadData = async () => {
      try {
        const typesResult = await dispatch(fetchWorkoutTypes()).unwrap();
        console.log('Workout types loaded:', typesResult);
        
        if (typesResult.length > 0) {
          const workoutsResult = await dispatch(fetchWorkouts()).unwrap();
          console.log('Workouts loaded:', workoutsResult);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [dispatch, selectedYear]);

  // 获取特定日期的运动记录
  const getWorkoutsForDate = (monthIndex, day) => {
    // Create date at start of day in local timezone
    const localDate = new Date(selectedYear, monthIndex, day);
    const formattedDate = localDate.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD in local timezone
    return workouts.filter(workout => workout.date === formattedDate);
  };

  // 处理日期点击
  const handleDateClick = (monthIndex, day) => {
    const localDate = new Date(selectedYear, monthIndex, day);
    setSelectedDate(localDate);
    setWorkoutSelectOpen(true);
  };

  // Add new function to handle workout selection
  const handleWorkoutSelect = async (typeId) => {
    try {
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      const currentWorkouts = getWorkoutsForDate(
        selectedDate.getMonth(), 
        selectedDate.getDate()
      );

      if (currentWorkouts.length > 0) {
        await dispatch(deleteWorkout(currentWorkouts[0].id)).unwrap();
      }

      if (typeId) {
        await dispatch(addWorkout({
          date: formattedDate,
          workout_type_id: typeId
        })).unwrap();
      }
      setWorkoutSelectOpen(false);
    } catch (error) {
      console.error('Error handling workout selection:', error);
    }
  };

  // Add workout type
  const handleAddType = async () => {
    try {
      await dispatch(addWorkoutType({
        name: newTypeName,
        color: newTypeColor,
        is_default: false
      })).unwrap();
      setNewTypeName('');
      setNewTypeColor('#000000');
      setOpenTypeDialog(false);
    } catch (error) {
      console.error('Error adding workout type:', error);
    }
  };

  // Delete workout type
  const handleDeleteType = async (typeId) => {
    try {
      await dispatch(deleteWorkoutType(typeId)).unwrap();
    } catch (error) {
      console.error('Error deleting workout type:', error);
    }
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // 添加导出功能
  const handleExport = async (type) => {
    const element = document.getElementById('fitness-calendar');
    
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });

      if (type === 'png') {
        // 导出为PNG
        const link = document.createElement('a');
        link.download = `fitness-tracker-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else if (type === 'pdf') {
        // 导出为PDF
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`fitness-tracker-${new Date().toISOString().split('T')[0]}.pdf`);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // 添加导出动作
  const actions = [
    { icon: <AddIcon />, name: 'Fitness Type', action: () => setOpenTypeDialog(true) },
    { icon: <PdfIcon />, name: 'Export PDF', action: () => handleExport('pdf') },
    { icon: <ImageIcon />, name: 'Export PNG', action: () => handleExport('png') },
  ];

  // if (!isDesktop) {
  //   return (
  //     <Container sx={{ py: 4, textAlign: 'center' }}>
  //       <Typography variant="h6" color="text.secondary">
  //         Please use a desktop device to access the Fitness Tracker
  //       </Typography>
  //     </Container>
  //   );
  // }

  return (
    <Container maxWidth="xl" sx={{ 
      py: { xs: 1, sm: 2 },
      px: { xs: 0.5, sm: 2 }  // Less padding on mobile
    }}>
      <Paper 
        id="fitness-calendar"
        sx={{ 
          p: { xs: 1, md: 1.5 }, 
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        {/* Header section */}
        <Stack 
          direction="row" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Fitness Tracker
            </Typography>
            <FormControl sx={{ minWidth: 100 }}>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                size="small"
                variant="outlined"
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {/* Calendar Grid with tighter spacing */}
        <Grid container spacing={{ xs: 0.5, sm: 1 }}>
          {/* First row - Jan to Apr */}
          {calendar.slice(0, 4).map((month, monthIndex) => (
            <Grid item xs={12} sm={6} md={3} key={month.name}>
              <MonthCalendar 
                month={month} 
                monthIndex={monthIndex} 
                selectedYear={selectedYear}
                handleDateClick={handleDateClick}
                getWorkoutsForDate={getWorkoutsForDate}
              />
            </Grid>
          ))}
          {/* Second row - May to Aug */}
          {calendar.slice(4, 8).map((month, monthIndex) => (
            <Grid item xs={12} sm={6} md={3} key={month.name}>
              <MonthCalendar 
                month={month} 
                monthIndex={monthIndex + 4}
                selectedYear={selectedYear}
                handleDateClick={handleDateClick}
                getWorkoutsForDate={getWorkoutsForDate}
              />
            </Grid>
          ))}
          {/* Third row - Sep to Dec */}
          {calendar.slice(8, 12).map((month, monthIndex) => (
            <Grid item xs={12} sm={6} md={3} key={month.name}>
              <MonthCalendar 
                month={month} 
                monthIndex={monthIndex + 8}
                selectedYear={selectedYear}
                handleDateClick={handleDateClick}
                getWorkoutsForDate={getWorkoutsForDate}
              />
            </Grid>
          ))}
        </Grid>

        {/* Workout Selection Dialog */}
        <Dialog 
          open={workoutSelectOpen} 
          onClose={() => setWorkoutSelectOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              m: { xs: 1, sm: 2 },  // Less margin on mobile
              width: { xs: 'calc(100% - 16px)', sm: 'auto' }
            }
          }}
        >
          <DialogTitle>
            Select Workout Type
            <Typography variant="subtitle2" color="text.secondary">
              {selectedDate?.toLocaleDateString()}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {[...workoutTypes]
                .sort((a, b) => {
                  // Sort default types first, then by name
                  if (a.is_default !== b.is_default) {
                    return b.is_default ? 1 : -1;
                  }
                  return a.name.localeCompare(b.name);
                })
                .map((type) => (
                  <Button
                    key={type.id}
                    onClick={() => handleWorkoutSelect(type.id)}
                    variant="outlined"
                    sx={{
                      height: 40,
                      justifyContent: 'flex-start',
                      borderColor: type.color,
                      color: 'text.primary',
                      backgroundColor: `${type.color}40`,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: `${type.color}59`,
                        borderColor: type.color,
                      }
                    }}
                    startIcon={
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: type.color,
                          opacity: 0.25
                        }}
                      />
                    }
                  >
                    {type.name}
                  </Button>
                ))}
              <Button
                onClick={() => handleWorkoutSelect(null)}
                variant="outlined"
                color="error"
                sx={{
                  height: 40,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  mt: 1
                }}
              >
                Clear Workout
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Legend with updated styling */}
        <Paper 
          elevation={0}
          sx={{ 
            mt: 2,
            p: 2, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',  // Space between types and button
            alignItems: 'center'
          }}
        >
          <Stack 
            direction="row" 
            flexWrap="wrap" 
            gap={3}
            alignItems="center"
            sx={{ flexGrow: 1 }}  // Take up remaining space
          >
            {[...workoutTypes]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((type) => (
                <Box 
                  key={type.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      backgroundColor: type.color,
                      opacity: 0.25
                    }}
                  />
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '1rem'
                    }}
                  >
                    {type.name}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Paper>

        {/* Workout Type Management Dialog */}
        <Dialog 
          open={openTypeDialog} 
          onClose={() => setOpenTypeDialog(false)}
          maxWidth="md"  // Make dialog wider
          fullWidth
        >
          <DialogTitle sx={{ 
            pb: 1,
            '& .MuiTypography-root': {
              fontSize: '1.25rem',
              fontWeight: 500
            }
          }}>
            Manage Fitness Types
          </DialogTitle>
          <DialogContent>
            {/* Single line form */}
            <Stack 
              direction="row" 
              spacing={2} 
              sx={{ mb: 3 }}
              alignItems="center"
            >
              <TextField
                label="Fitness Type Name"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                size="medium"  // Bigger input
                sx={{ flexGrow: 1 }}
              />
              <TextField
                type="color"
                value={newTypeColor}
                onChange={(e) => setNewTypeColor(e.target.value)}
                sx={{
                  width: 56,  // Bigger color picker
                  height: 56,
                  '& input': {
                    width: 56,
                    height: 56,
                    padding: 0,
                    border: 'none',
                    borderRadius: 1,
                    cursor: 'pointer'
                  }
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleAddType}
                disabled={!newTypeName.trim()}
                sx={{
                  height: 56,  // Match height
                  fontSize: '1rem',
                  borderRadius: 1.5,
                  px: 4,
                  textTransform: 'none'
                }}
              >
                Add Fitness Type
              </Button>
            </Stack>

            {/* List of existing types */}
            <List sx={{ 
              maxHeight: 400, 
              overflowY: 'auto',
              '& .MuiListItem-root': {
                py: 2  // Bigger list items
              }
            }}>
              {[...workoutTypes]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((type) => (
                  <ListItem key={type.id}>
                    <Box
                      sx={{
                        width: 32,  // Bigger color boxes
                        height: 32,
                        borderRadius: 1,
                        backgroundColor: type.color,
                        opacity: 0.25,
                        mr: 2
                      }}
                    />
                    <ListItemText 
                      primary={type.name}
                      primaryTypographyProps={{
                        fontSize: '1rem'  // Bigger text
                      }}
                    />
                    {!type.is_default && (
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteType(type.id)}
                          sx={{
                            width: 40,  // Bigger delete button
                            height: 40,
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'error.main',
                              backgroundColor: 'error.lighter'
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1.4rem' }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: 1, 
            borderColor: 'divider',
            px: 3,
            py: 2
          }}>
            <Button 
              onClick={() => setOpenTypeDialog(false)}
              variant="contained"
              sx={{
                height: 48,
                borderRadius: 1.5,
                textTransform: 'none',
                px: 4,
                fontSize: '1rem'
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      {/* Move SpeedDial outside of the Paper component */}
      <SpeedDial
        ariaLabel="Fitness Options"
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          '& .MuiFab-primary': {
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            }
          },
          '& .MuiSpeedDial-actions': {
            gap: 1, // Add some gap between actions
          }
        }}
        icon={<SpeedDialIcon />}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
            tooltipOpen
            sx={{
              '& .MuiSpeedDialAction-staticTooltipLabel': {
                backgroundColor: 'background.paper',
                color: 'text.primary',
                boxShadow: 1,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
              }
            }}
          />
        ))}
      </SpeedDial>
    </Container>
  );
};

// Separate component for each month's calendar
const MonthCalendar = ({ month, monthIndex, selectedYear, handleDateClick, getWorkoutsForDate }) => {
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  const generateMonthData = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = Array(firstDay).fill(null);
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    const remainingDays = 42 - days.length;
    days.push(...Array(remainingDays).fill(null));
    
    return days;
  };

  return (
    <Paper elevation={1} sx={{ 
      p: { xs: 0.5, sm: 1 },  // Less padding on mobile
      height: '100%' 
    }}>
      <Typography 
        variant="subtitle2" 
        align="center" 
        sx={{ 
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          fontWeight: 500,
          mb: { xs: 0.25, sm: 0.5 },
          color: 'text.primary' 
        }}
      >
        {month.name}
      </Typography>
      
      <Grid container spacing={0}>
        {weekDays.map(day => (
          <Grid item xs={12/7} key={day}>
            <Box sx={{ 
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: { xs: '0.5rem', sm: '0.6rem' },
              mb: { xs: 0.1, sm: 0.25 }
            }}>
              {day}
            </Box>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 0.1, sm: 0.25 }}>
        {generateMonthData(selectedYear, monthIndex).map((day, index) => (
          <Grid item xs={12/7} key={index}>
            {day !== null ? (
              <Tooltip
                title={
                  getWorkoutsForDate(monthIndex, day).map(workout => 
                    workout.workout_type.name
                  ).join(', ') || 'No workout'
                }
              >
                <Box
                  onClick={() => handleDateClick(monthIndex, day)}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    cursor: 'pointer',
                    borderRadius: { xs: 0.5, sm: 1 },
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: { xs: 28, sm: 32, md: 36 }, // Smaller on mobile
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  {getWorkoutsForDate(monthIndex, day).map((workout, wIndex) => (
                    <Box
                      key={wIndex}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: workout.workout_type.color,
                        opacity: 0.25,
                        borderRadius: 1,
                      }}
                    />
                  ))}
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      fontWeight: 500,
                      color: 'text.primary',
                      zIndex: 1
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <Box sx={{ paddingTop: '100%' }} />
            )}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default Fitness; 